import React from 'react';

import { Translate } from 'react-localize-redux';

import { SortableContainer, SortableElement } from 'react-sortable-hoc';

import styled from 'styled-components';
import { CloseMenu } from '@webapp/store/ui/action-creators';
import { TracPageLink } from '@services/tracpage/types/trac-page-link';

import Menu from '@components/menu';
import InputField from '@components/form/input';
import Typography from '@components/basics/typography';
import DefaultAvatar from '@components/basics/default-avatar';
import SortableHandleButton from '@components/basics/sortable-button';
import CloseIcon from '@components/basics/icons/close';
import DotsCircleIcon from '@components/basics/icons/dots-circle';

import LoadingCtrl from '@webapp/controllers/loading';
export type LinkWithTitle = TracPageLink & { showTitle?: boolean };

const SortableLinkItem = SortableElement(
  ({
    link,
    withHandle,
    handleChange,
    handleRemove,
    closeMenu,
    isLoading,
    setOnKeyEnter,
  }: {
    withHandle: boolean;
    link: LinkWithTitle;
    isLoading: boolean;
    closeMenu: () => CloseMenu;
    handleRemove: (order: number) => void;
    handleChange: <T extends keyof TracPageLink>(
      order: number,
      field: T,
      value: TracPageLink[T]
    ) => void;
    setOnKeyEnter: React.Dispatch<React.SetStateAction<boolean>>;
  }) => {
    const [imgError, setImgError] = React.useState<boolean>(false);

    const leftDecoration = (
      <div>
        {imgError ? (
          <DefaultAvatarIcon />
        ) : (
          <Avatar
            src={link.avatar}
            alt={link.title}
            onError={() => setImgError(true)}
          />
        )}
      </div>
    );

    const rightDecoration = (
      <Menu
        className="menu"
        button={<DotsCircleIcon className="open-icon" />}
        closeButton={<CloseIcon onClick={closeMenu} className="close-icon" />}
        customIconHeight="24px"
        menuId={`LINK-MENU-${link.order}`}
      >
        <MenuContent>
          <Typography weight="700" className="menu-item">
            <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.LINKS.MENU.EDIT" />
          </Typography>
          <Typography
            weight="700"
            className="menu-item danger"
            onClick={() => handleRemove(link.order)}
          >
            <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.LINKS.MENU.DELETE" />
          </Typography>
        </MenuContent>
      </Menu>
    );

    return (
      <LinkItem key={`link-${link.order}`}>
        <div className="link-input">
          <LinkWrapper>
            <InputField
              className="link-input"
              name="link"
              label={
                <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.LINKS.INPUT.LINK.LABEL" />
              }
              value={link.url === '' ? 'https://' : link.url}
              autoComplete="off"
              locked={isLoading}
              rightDecoration={rightDecoration}
              onChange={(value) => handleChange(link.order, 'url', value)}
              onKeyDown={(event) => {
                if (event?.key === 'Enter' && setOnKeyEnter) {
                  setOnKeyEnter(true);
                }
              }}
              onBlur={() => {
                if (setOnKeyEnter) setOnKeyEnter(true);
              }}
            />
            {isLoading && (
              <LoadingCtrl size="48px" className="loading-icon" loading />
            )}
          </LinkWrapper>
          {(link.embedData || link.showTitle || link.title.length > 0) && (
            <InputField
              name="title"
              locked={isLoading}
              label={
                <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.LINKS.INPUT.TITLE.LABEL" />
              }
              leftDecoration={leftDecoration}
              onChange={(value) => handleChange(link.order, 'title', value)}
              autoComplete="off"
              value={link.title}
            />
          )}
        </div>
        {withHandle && <SortableHandleButton />}
      </LinkItem>
    );
  }
);

const SortableLinks = SortableContainer(
  ({
    links,
    handleChange,
    handleRemove,
    closeMenu,
    loaderData,
    setOnKeyEnter,
  }: {
    links: LinkWithTitle[];
    handleRemove: (order: number) => void;
    handleChange: <T extends keyof TracPageLink>(
      order: number,
      field: T,
      value: TracPageLink[T]
    ) => void;
    closeMenu: () => CloseMenu;
    loaderData: { isLoading: boolean; lastIdUpdated?: number };
    setOnKeyEnter: React.Dispatch<React.SetStateAction<boolean>>;
  }) => {
    return (
      <SorteableLinksWrapper>
        {links.map((link, i) => (
          <SortableLinkItem
            link={link}
            index={i}
            key={i}
            withHandle={links.length > 1}
            handleChange={handleChange}
            handleRemove={handleRemove}
            isLoading={
              loaderData.isLoading &&
              loaderData.lastIdUpdated !== undefined &&
              link.order === loaderData.lastIdUpdated
            }
            setOnKeyEnter={setOnKeyEnter}
            closeMenu={closeMenu}
          />
        ))}
      </SorteableLinksWrapper>
    );
  }
);

export default SortableLinks;

const LinkItem = styled.div`
  position: relative;

  > span {
    opacity: 0;
  }

  &:hover > span {
    opacity: 1;
  }
`;

const Avatar = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  object-fit: cover;
`;

const DefaultAvatarIcon = styled(DefaultAvatar)`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  object-fit: cover;
`;

const LinkWrapper = styled.div`
  position: relative;
  margin-bottom: 3px;

  .link-input {
    z-index: 10;
  }

  .loading-icon {
    position: absolute;
    right: 0;
    top: 0;
  }

  input {
    padding-right: 48px;
  }

  .arrow {
    position: absolute;
    top: 50%;
    right: 0;
    transform: translate(0, -50%);
    height: 24px;
    width: 24px;
  }
`;

const SorteableLinksWrapper = styled.div`
  display: grid;
  grid-gap: 20px;
`;

const MenuContent = styled.div`
  display: grid;
  grid-gap: 5px;
  min-width: 128px;
  z-index: 9999;

  .menu-item {
    cursor: pointer;
    transition: color
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};

    &:hover {
      color: ${({ theme }) => theme.colors.tertiary};
    }

    &.danger {
      color: ${({ theme }) => theme.colors.accent};
      margin-top: 14px;
      &:hover {
        color: ${({ theme }) => theme.colors.accentHover};
      }
    }
  }
`;
